import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DairyService, { IBank } from '@src/api/services/dairy';
import { Icon } from 'semantic-ui-react';
import { BankEl } from './style';
import { useNavigate } from 'react-router-dom';
import { RouteTypes } from '~/routes/constants/index';
import CreateEditBanks from './CreateEditBanks';
import { GlobalEl } from '~/styles/app';
import notify from '@src/components/Notify';
import Dialog from '@src/components/Dialog';
import CreateEditBankAccounts from './CreateEditBankAccount';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import i18n from '~/components/i18n';
import TableComponent from '~/components/TableComponent';
import { El as LayoutEl } from '~/components/Layout/style';
import Layout from '~/components/Layout';
import { ADMIN_ROLE_SLUG } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import { handleErrors } from '~/utils/helpers';

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit',
    displayedText: i18n.t('No actions')
  },
  {
    popupMessage: i18n.t('Enable'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'disableEnable',
    display: [0]
  },
  {
    popupMessage: i18n.t('Disable'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'disableEnable',
    display: [1]
  }
]

const geTableColumns = () => [
  {
    name: i18n.t('IBAN'),
    key: 'iban'
  },
  {
    name: i18n.t('SWIFT'),
    key: 'swift'
  },
  {
    name: i18n.t('OTC'),
    key: 'otc',
    type: 'verified'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'verified'
  },
  {
    name: i18n.t('Actions')
  }
];

const DairyFactoryProfileBanks: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isBusy, setIsBusy] = useState(false);
  const [drawer, setDrawerState] = useState({
    isVisible: false,
    id: null
  });
  const [bankAccountDrawer, setBankAccountDrawerState] = useState({
    isVisible: false,
    id: '',
    idAccount: null
  });
  const [isDialogOpen, setIsDialogOpenState] = useState<boolean>(false);
  const [selectedBank, setSelectedBankState] = useState<IBank>({
    id: '',
    name: ''
  });
  const [blockAccount, setBlockAccount] = useState(null);
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const adminLogged = roleSlug === ADMIN_ROLE_SLUG

  const onSuccess = (data) => {
    notify(t('Successfully deleted bank'), 'success');
    dairyBanksData();
  };

  const {
    data: banks,
    refetch: dairyBanksData
  } = DairyQuery.getDairyBanks();

  const { isLoading: isBankDeleteLoading, mutate: deleteBankById } = DairyQuery.deleteDairyBankById(
    onSuccess
  );

  const onBankDelete = async () => {
    deleteBankById(selectedBank.id);
    setIsDialogOpenState(false);
  };

  // Generate letters for Banks - Max 3 characters
  const generateLetters = (name: string) => {
    const letters = name
      .toUpperCase()
      .match(/\b(\w)/g)
      .join('');

    return letters.substring(0, 3);
  };

  const onCloseDialog = () => {
    setIsDialogOpenState(false);
  };

  function handleActionClick(row, type) {
    const { id } = row
    switch (type) {
      case 'edit':
        return setBankAccountDrawerState({ isVisible: true, id: id, idAccount: id })
      case 'disableEnable':
        return setBlockAccount(row)
      default:
        return
    }
  }

  const renderTable = (bank: IBank) => {
    return (
      bank.accounts.length > 0 && (
        <TableComponent
          tableColumnHeaders={geTableColumns()}
          rows={bank.accounts}
          removePagination
          actions={adminLogged ? getActions() : []}
          total={bank.accounts.length}
          handleClickAction={(row, type) => handleActionClick(row, type)}
        />
      )
    );
  };

  const renderBoxContent = () => {
    return banks?.rows.map((bank) => {
      const accountLabel = bank.accounts.length > 1 ? t('Accounts') : t('Account')
      return (
        <BankEl.BankBox key={bank.id}>
          <div className='bank-box-header'>
            <div className='bank-logo'>{generateLetters(bank.name)}</div>
            <div className='bank-content'>
              <div className='bank-name'>{bank.name}</div>
            </div>
            <div className='bank-accounts-counter'>
              {bank.accounts.length || 0} {accountLabel}
            </div>
            {adminLogged && <div className='bank-options'>
              <GlobalEl.DefaultOutlineButton
                className='mid-size'
                onClick={() => {
                  setBankAccountDrawerState({
                    isVisible: true,
                    id: bank.id,
                    idAccount: null
                  });
                }}
              >
                <Icon className='btn-icon' name='plus square outline'></Icon>
                {t('Add new Account')}
              </GlobalEl.DefaultOutlineButton>
              <GlobalEl.DefaultLinkButton
                className='ml-8'
                onClick={() => {
                  navigate(RouteTypes.DAIRY_FACTORY_PROFILE_BANKS_EDIT.replace(':id', bank.id), {
                    replace: true,
                    state: { status: 'open' }
                  });
                  setDrawerState({ isVisible: true, id: bank.id });
                }}
              >
                <Icon className='btn-icon' name='pencil alternate'></Icon>
                {t('Edit')}
              </GlobalEl.DefaultLinkButton>
              <GlobalEl.DefaultLinkButton
                className='danger'
                onClick={() => {
                  setSelectedBankState(bank);
                  setIsDialogOpenState(true);
                }}
              >
                <Icon className='btn-icon' name='trash alternate outline'></Icon>
                {t('Delete')}
              </GlobalEl.DefaultLinkButton>
            </div>}
          </div>
          {renderTable(bank)}
        </BankEl.BankBox>
      );
    });
  };

  function renderActions() {
    return <LayoutEl.PageHeaderActionButton
      type='button'
      onClick={() => {
        navigate(RouteTypes.DAIRY_FACTORY_PROFILE_BANKS_CREATE, { state: { status: 'open' } });
        setDrawerState({ isVisible: true, id: null });
      }}
    >
      {t('Add new bank')}
    </LayoutEl.PageHeaderActionButton>
  }

  async function handleBlockAccount() {
    setIsBusy(true);
    try {
      if (blockAccount.status === 1) {
        await DairyService.disableDairyAccount(blockAccount.id);
      } else {
        await DairyService.enableDairyAccount(blockAccount.id);
      }
      setBlockAccount(null);
      dairyBanksData();
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderBlockModal() {
    const { status } = blockAccount;
    const header = status === 1 ? t('Disable') : t('Enable');

    return <Dialog
      isDialogOpen={Boolean(blockAccount)}
      textClose={t('No')}
      isLoading={isBusy}
      handleClose={() => setBlockAccount(null)}
      textSubmit={t('Yes')}
      handleSubmit={() => handleBlockAccount()}
      textContent={t('{{header}} account ?', { header })}
      textHeader={header}
      type={'abort'}
    />
  }

  return (
    <>
      <Layout
        actions={adminLogged && renderActions()}
        title={t('Banks')}
        childContainerSize={'auto'}
        fontSizeHeader={'18px'}>
        {blockAccount && renderBlockModal()}
        {banks && renderBoxContent()}
        <CreateEditBanks
          isVisible={drawer.isVisible}
          id={drawer.id}
          fetchData={dairyBanksData}
          handleHiddenDrawer={() => {
            navigate(RouteTypes.DAIRY_FACTORY_PROFILE_BANKS);
            setDrawerState({ isVisible: false, id: null });
          }}
        />
        <CreateEditBankAccounts
          isVisible={bankAccountDrawer.isVisible}
          bankId={bankAccountDrawer.id}
          fetchData={dairyBanksData}
          idAccount={bankAccountDrawer.idAccount}
          handleHiddenDrawer={() => {
            setBankAccountDrawerState({ isVisible: false, id: null, idAccount: null });
            dairyBanksData();
          }}
        />
        <Dialog
          textHeader={t(`Delete Bank: ${selectedBank.name}`)}
          textClose={t('Close')}
          textContent={t(`Are you sure that you want to delete ${selectedBank.name} bank?`)}
          textSubmit={t('Delete Bank')}
          handleClose={onCloseDialog}
          handleSubmit={onBankDelete}
          size={'small'}
          type='abort'
          isDialogOpen={isDialogOpen}
          isLoading={isBankDeleteLoading}
        />
      </Layout>
    </>
  );
};

export default DairyFactoryProfileBanks;
